import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Field, FieldError, FieldLabel } from '@/components/shadcn/field';
import { Input } from '@/components/shadcn/input';
import isEmpty from '@/lib/utils/isEmpty';

import type { CreatePartyValues } from './types';
import useRequiredByCrm from './useRequiredByCrm';

const FIELDS_DEPENDING_ON_WHOLE_ADDRESS: Array<
  'address.street' | 'address.housenr'
> = ['address.street', 'address.housenr'];

function hasAnyAddressPart(address: CreatePartyValues['address']) {
  const parts = [
    address?.street,
    address?.housenr,
    address?.boxnr,
    address?.zipcode,
    address?.city,
  ];

  return parts.some((part) => !isEmpty(part));
}

export default function AddressFields() {
  const { t } = useTranslation();

  const {
    register,
    formState: { errors },
  } = useFormContext<CreatePartyValues>();

  const requiredByCrm = useRequiredByCrm();

  const address = useWatch<CreatePartyValues, 'address'>({ name: 'address' });
  const isAddressStarted = hasAnyAddressPart(address);

  const streetLabel = t('PARTY_CREATE_INLINE_STREET_LABEL');
  const houseNumberLabel = t('PARTY_CREATE_INLINE_HOUSE_NUMBER_LABEL');
  const zipcodeLabel = t('PARTY_CREATE_INLINE_ZIPCODE_LABEL');
  const cityLabel = t('PARTY_CREATE_INLINE_CITY_LABEL');

  const streetRequirement = requiredByCrm('address.street', streetLabel);
  const houseNumberRequirement = requiredByCrm(
    'address.housenr',
    houseNumberLabel,
  );
  const zipcodeRequirement = requiredByCrm('address.zipcode', zipcodeLabel);
  const cityRequirement = requiredByCrm('address.city', cityLabel);

  const isStreetRequired = streetRequirement.isRequired || isAddressStarted;
  const isHouseNumberRequired =
    houseNumberRequirement.isRequired || isAddressStarted;

  const requiredOnceAddressIsStarted = (
    value: Maybe<string>,
    formValues: CreatePartyValues,
  ) => {
    if (!isEmpty(value) || !hasAnyAddressPart(formValues.address)) return true;

    return t('PARTY_CREATE_INLINE_ADDRESS_INCOMPLETE');
  };

  const addressErrors = errors.address;

  return (
    <div className="grid grid-cols-2 items-start gap-2">
      <Field
        className="col-span-2"
        data-invalid={addressErrors?.street ? true : undefined}
      >
        <FieldLabel htmlFor="street">
          {streetLabel}
          {isStreetRequired && ' *'}
        </FieldLabel>

        <Input
          id="street"
          {...register('address.street', {
            deps: FIELDS_DEPENDING_ON_WHOLE_ADDRESS,
            validate: {
              requiredByCrm: streetRequirement.validate,
              requiredOnceAddressIsStarted,
            },
          })}
        />

        <FieldError
          errors={addressErrors?.street ? [addressErrors.street] : undefined}
        />
      </Field>

      <Field data-invalid={addressErrors?.housenr ? true : undefined}>
        <FieldLabel htmlFor="housenr">
          {houseNumberLabel}
          {isHouseNumberRequired && ' *'}
        </FieldLabel>

        <Input
          id="housenr"
          {...register('address.housenr', {
            deps: FIELDS_DEPENDING_ON_WHOLE_ADDRESS,
            validate: {
              requiredByCrm: houseNumberRequirement.validate,
              requiredOnceAddressIsStarted,
            },
          })}
        />

        <FieldError
          errors={addressErrors?.housenr ? [addressErrors.housenr] : undefined}
        />
      </Field>

      <Field>
        <FieldLabel htmlFor="boxnr">
          {t('PARTY_CREATE_INLINE_BOX_NUMBER_LABEL')}
        </FieldLabel>

        <Input
          id="boxnr"
          {...register('address.boxnr', {
            deps: FIELDS_DEPENDING_ON_WHOLE_ADDRESS,
          })}
        />
      </Field>

      <Field data-invalid={addressErrors?.zipcode ? true : undefined}>
        <FieldLabel htmlFor="zipcode">
          {zipcodeLabel}
          {zipcodeRequirement.isRequired && ' *'}
        </FieldLabel>

        <Input
          id="zipcode"
          {...register('address.zipcode', {
            deps: FIELDS_DEPENDING_ON_WHOLE_ADDRESS,
            validate: zipcodeRequirement.validate,
          })}
        />

        <FieldError
          errors={addressErrors?.zipcode ? [addressErrors.zipcode] : undefined}
        />
      </Field>

      <Field data-invalid={addressErrors?.city ? true : undefined}>
        <FieldLabel htmlFor="city">
          {cityLabel}
          {cityRequirement.isRequired && ' *'}
        </FieldLabel>

        <Input
          id="city"
          {...register('address.city', {
            deps: FIELDS_DEPENDING_ON_WHOLE_ADDRESS,
            validate: cityRequirement.validate,
          })}
        />

        <FieldError
          errors={addressErrors?.city ? [addressErrors.city] : undefined}
        />
      </Field>
    </div>
  );
}
